import pkg, { ExperimentsConfig, setPackageState } from "./pkg";

export {
  getAssignmentBucket,
  getBucketMap,
  getBucket,
  BucketMap,
} from "./BucketingTasks";

export const HOLDOUT = "holdout" as const;

export type ExperimentVariants<T extends string = string> = T;
export type DefaultVariants = "control" | "treatment";

export const EXPERIMENT_ASSIGNMENTS_LOCAL_STORAGE_NAME =
  "experimentAssignments";
export const LOGGED_IN_USER_EXP_COOKIE_NAME = "loggedInUserExperimentUid";
export const LOGGED_OUT_USER_EXP_COOKIE_NAME = "loggedOutUserExperimentUid";

export {
  sendExperimentTriggerThroughEventTracking,
  EVENT_SOURCE,
} from "./experimentTriggers";
export function init(config: ExperimentsConfig) {
  if (pkg.initialized) {
    console.warn("@outschool/experiments-shared has already been initialized");
  } else {
    setPackageState(config);
  }
}

export enum RandomizationUnits {
  /** The activity_uid from the activities table */
  Activity = "Activity",
  /** The slug_id from the activities table */
  ActivitySlug = "ActivitySlug",
  /**
   * Applies to both logged in and logged out users
   * uses the experiment_uid stored in browser storage on the user's device
   */
  AllUsersAnonymousId = "AllUsersAnonymousId",
  /** The learner_uid from the learners table */
  Learner = "Learner",
  /**
   * The user_uid from the users table if the user is logged in,
   * else the experiment_uid stored in browser storage on the user's device
   */
  LoggedInOrLoggedOutUser = "LoggedInOrLoggedOutUser",
  /** The user_uid from the users table */
  LoggedInUser = "LoggedInUser",
  /** The experiment_uid stored in browser storage on the user's device */
  LoggedOutUser = "LoggedOutUser",
}

export type AssignmentRequest = Array<{
  randomizationUnit: RandomizationUnits;
  randomizationUnitIds: string[];
}>;

type AssignmentResponse = Array<{
  randomizationUnit: RandomizationUnits;
  randomizationUnitId: string;
  experimentName: string;
  variantAssigned: string;
}>;

export type ExperimentAssignments = Record<string, string>;

/**
 * Throws error in the event that the timeout exceeds given timeoutMs.
 * @param fetchParams Paremeters to include with the fetch call.
 * @param timeoutMs Timeout time in ms
 * @returns Promise with Response.
 */
async function fetchWithTimeout(
  fetchParams: Parameters<typeof fetch>,
  debugDescriptor: string,
  timeoutMs: number = 500,
) {
  const [input, init] = fetchParams;
  let response: Response | undefined;

  // AbortSignal.timeout isn't supported in all browsers, so fall back to a custom implementation.
  const abortSignalWithTimeout =
    "timeout" in AbortSignal
      ? AbortSignal.timeout
      : (ms: number) => {
          const ctrl = new AbortController();
          // Abort with "TimeoutError" to match the native AbortSignal.timeout behavior.
          setTimeout(() => ctrl.abort(new DOMException("TimeoutError")), ms);
          return ctrl.signal;
        };

  try {
    response = await fetch(input, {
      ...init,
      signal: abortSignalWithTimeout(timeoutMs),
    });
  } catch (error) {
    // TimeoutError: thrown by native AbortSignal.timeout when timeout exceeded
    if (error instanceof Error && error.name === "TimeoutError") {
      throw new Error(
        `Experiments service timed out in ${timeoutMs}ms: ${debugDescriptor}`,
      );
    }
    // AbortError: thrown by fallback AbortController (also a timeout)
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(
        `Experiments service request aborted: ${debugDescriptor}`,
      );
    }
    // Network errors (TypeError): DNS, CORS, connection refused, etc.
    if (error instanceof TypeError) {
      throw new Error(`Experiments service network error: ${debugDescriptor}`);
    }
    // Re-throw unknown errors with cause chain preserved
    throw error;
  }
  return response;
}

export interface MakeGetRequestInterface {
  randomizationUnit: RandomizationUnits;
  randomizationUnitId: string;
  experimentsServiceUrl: string;
  experimentsApiKey: string;
  debugDescriptor?: string;
  timeoutMs?: number;
}

export async function makeGetRequest({
  randomizationUnit,
  randomizationUnitId,
  experimentsServiceUrl,
  experimentsApiKey,
  debugDescriptor = "makeGetRequest",
  timeoutMs = undefined,
}: MakeGetRequestInterface): Promise<ExperimentAssignments> {
  // guard against undefined values that should not be there that will ultimately return a 400
  // loggedInUserExpUid needs to always be defined, loggedInUserExpUid can be undefined in the case
  // of a user that has never logged in

  if (!randomizationUnitId) {
    return {};
  }

  const resp = await fetchWithTimeout(
    [
      `${experimentsServiceUrl}/assignment/${randomizationUnit}/${randomizationUnitId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${experimentsApiKey}`,
        },
      },
    ],
    debugDescriptor,
    timeoutMs,
  );

  const assignmentResponse: AssignmentResponse = await resp.json();
  return assignmentResponse.reduce((obj: ExperimentAssignments, assignment) => {
    obj[assignment.experimentName] = assignment.variantAssigned;
    return obj;
  }, {} as ExperimentAssignments);
}

export async function fetchWebsiteExpAssignment({
  loggedInUserExpUid,
  loggedOutUserExpUid,
  experimentsServiceUrl,
  timeoutMs = undefined,
}: {
  loggedInUserExpUid?: string;
  loggedOutUserExpUid?: string;
  experimentsServiceUrl: string;
  timeoutMs?: number;
}): Promise<ExperimentAssignments> {
  // guard against undefined values that should not be there that will ultimately return a 400
  // loggedInUserExpUid needs to always be defined, loggedInUserExpUid can be undefined in the case
  // of a user that has never logged in

  if (!loggedOutUserExpUid) {
    return {};
  }

  const resp = await fetchWithTimeout(
    [
      `${experimentsServiceUrl}/assignment/website/${loggedOutUserExpUid}/${loggedInUserExpUid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    ],
    "fetchWebsiteExpAssignment",
    timeoutMs,
  );

  const assignmentResponse: AssignmentResponse = await resp.json();
  return assignmentResponse.reduce((obj: ExperimentAssignments, assignment) => {
    obj[assignment.experimentName] = assignment.variantAssigned;
    return obj;
  }, {} as ExperimentAssignments);
}

export function fetchLearnerExpAssignments({
  learnerUid,
  experimentsServiceUrl,
  experimentsApiKey,
  timeoutMs = undefined,
}: {
  learnerUid: string;
  experimentsServiceUrl: string;
  experimentsApiKey: string;
  timeoutMs?: number;
}): Promise<ExperimentAssignments> {
  return makeGetRequest({
    randomizationUnit: RandomizationUnits.Learner,
    randomizationUnitId: learnerUid,
    experimentsServiceUrl,
    experimentsApiKey,
    debugDescriptor: "fetchLearnerExpAssignments",
    timeoutMs,
  });
}
import ldZipObject from "lodash/zipObject";

/**
 * The experiment state dictionary is encoded in the search params `experimentName` and `experimentAssigned`.
 * It looks like this:
 * outschool.com/?experimentName=experiment1,experiment2&variantAssigned=control,treatment
 * To decode the dictionary you have to [zipObject](https://lodash.com/docs/4.17.15#zipObject) the values of experiment name and variant assigned
 * `zip([experiment1, experiment2],[control,treatment])`
 * result: {experiment1: control, experiment2: treatment}
 *
 */
export function encodeExperimentAssignmentUrlQueryParams(
  url: URL,
  experimentAssignments: Record<string, string> = {},
): void {
  const experimentNames: string[] = Object.keys(experimentAssignments).sort();
  const variantsAssigned: string[] = [];
  for (const experimentName of experimentNames) {
    variantsAssigned.push(experimentAssignments[experimentName]);
  }

  if (experimentNames.length) {
    url.searchParams.set("experimentName", experimentNames.join(","));
  }
  if (variantsAssigned.length) {
    url.searchParams.set("variantAssigned", variantsAssigned.join(","));
  }
}

export function decodeExperimentAssignmentUrlQueryParams(
  url: URL,
): Record<string, string> {
  let experimentAssignments: Record<string, string> = {};

  if (!!url) {
    const parseStrToArray = (input: string | null) => {
      if (!input) {
        return [];
      }
      return input.split(",");
    };

    // tempUrl is only used to grab the searchparams passed from ssr-cloudlflare-worker
    const tempUrl = new URL(url, "http://example.com");
    const experimentNames = parseStrToArray(
      tempUrl.searchParams.get("experimentName"),
    );
    const variantsAssigned = parseStrToArray(
      tempUrl.searchParams.get("variantAssigned"),
    );
    if (experimentNames.length !== variantsAssigned.length) {
      console.log(
        `Experiment name length != variants assigned | experimentNames: ${experimentNames} | variantsAssigned: ${variantsAssigned}`,
      );
      experimentAssignments = {};
    }
    experimentAssignments = ldZipObject(experimentNames, variantsAssigned);
  }
  return experimentAssignments;
}
